import React from 'react';
import PropTypes from 'prop-types';
import { useLocation, Link } from 'react-router-dom';
import { observer } from 'mobx-react';

import backIcon from 'core/assets/svg/ic-back-40px.svg';
import { Label } from 'core/components/label';
import { Badge } from 'core/components/badge';
import { isCondensedLayout } from 'core/components/theme/condensed-layout';
import { dateTimeFormatterInUserTimezone } from 'core/helpers/formatters';
import { IconButtonWithTooltip } from 'core/components/icon-button-with-tooltip';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import useMemberMatchSearch from 'offer/components/header/use-member-match-search';
import { getCoappName, simulatedSourceLabels } from 'offer/components/header/header.helpers';
import { policyTypeLabelMap } from 'offer/components/sidebar/components/policy-picker-card/policy-picker-card';
import { MemberMatches } from './member-matches';
import { SupportingDocuments } from './supporting-documents';
import QuoteFacts from './quote-facts';
import useHideSiteHeaderOnScroll from './use-hide-site-header-on-scroll';
import useStyles from './header.styles';

/**
 * Staff Quote Revisit header (#36364) for Modify Quote and Checkout: who the quote is for (name,
 * co-applicant, member matches) on top, and where it came from (pre-bind ID, rep, quote source,
 * referrer) underneath. Scrolling down slides the site header away; see
 * use-hide-site-header-on-scroll.
 *
 * Internal staff can edit the quote source and referrer and see supporting documents. Agency users
 * see the quote source read-only and don't get the referrer or supporting documents.
 *
 * Condensed layout: a breadcrumb replaces the back button, the title reads "Quote for NAME" over the selected
 * option and when the offer was last saved, and the pre-bind ID row moves to the side column (QuoteFacts).
 */
const Header = ({ offerId, history, onBack }) => {
  const classes = useStyles();
  const location = useLocation();
  const { isAgency, canSimulateSource } = useSession();
  const fromCheckout = location.pathname.includes('checkout');
  const condensed = isCondensedLayout();
  const {
    offer: { loading, offer, selectedOption }
  } = useStore();

  useMemberMatchSearch();
  useHideSiteHeaderOnScroll();

  const quote = offer?.quote;
  const existingCustomerId = quote?.existingCustomer?.id;
  const applicantName = quote?.fname && quote?.lname ? `${quote.fname} ${quote.lname}` : null;
  const isLoaded = applicantName && !(loading && !offer);
  const coappName = getCoappName(quote?.drivers);
  const simulatedSource = canSimulateSource && quote?.simulationConfig?.source;

  let title = 'Loading...';
  if (isLoaded && condensed) {
    title = `Quote for ${applicantName}`;
  } else if (isLoaded) {
    title = existingCustomerId ? (
      <Link className={classes.titleLink} to={`/customer/${existingCustomerId}`}>
        {applicantName}
      </Link>
    ) : (
      applicantName
    );
  }

  return (
    <header id="offer_header" className={classes.header}>
      {condensed && (
        <nav aria-label="Breadcrumb" className={classes.breadcrumb}>
          {existingCustomerId && applicantName ? (
            <Link to={`/customer/${existingCustomerId}`}>{applicantName}</Link>
          ) : (
            <Link to="/search/offers">Search</Link>
          )}
          <span aria-hidden="true">/</span>
          <span aria-current="page">Quote</span>
        </nav>
      )}
      <div className={classes.rowTop}>
        {!condensed && (
          <IconButtonWithTooltip
            tooltip="Go back to previous page"
            icon={backIcon}
            onClick={onBack}
            iconClassName={classes.backButton}
            imgClassName={classes.backIcon}
            placement="right"
          />
        )}
        <div className={classes.colLeft}>
          <Label type="title" component="h1" className={classes.title}>
            {title}
          </Label>
          {condensed && isLoaded && (
            <Label type="body2" component="p" className={classes.subtitle}>
              {[
                policyTypeLabelMap[selectedOption],
                offer.updatedDateTime &&
                  `updated ${dateTimeFormatterInUserTimezone(offer.updatedDateTime, { separator: ' at ' })}`
              ]
                .filter(Boolean)
                .join(' · ')}
            </Label>
          )}
          {isLoaded && coappName && (
            <div className={classes.coApplicant}>
              <Label type="infoLabel" className={classes.coApplicantLabel}>
                Co-applicant
              </Label>
              <Label type="infoSubtitle" className={classes.coApplicantName}>
                {coappName}
              </Label>
            </div>
          )}
          {isLoaded && simulatedSource && (
            <Badge className={classes.simulatedSourceBadge}>
              {simulatedSourceLabels[simulatedSource] || simulatedSource}
            </Badge>
          )}
        </div>
        <div className={classes.colRight}>
          <MemberMatches fromCheckout={fromCheckout} offerId={offerId} history={history} />
          {!isAgency && <SupportingDocuments />}
        </div>
      </div>
      {isLoaded && !condensed && <QuoteFacts fromCheckout={fromCheckout} />}
    </header>
  );
};

Header.propTypes = {
  offerId: PropTypes.string.isRequired,
  history: PropTypes.object,
  onBack: PropTypes.func
};

Header.defaultProps = {
  history: {},
  onBack: () => {}
};

export default observer(Header);
